import { Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui/ErrorFallback'
import useEnterPageLogAnalytics from '@/shared/hooks/useEnterPageLogAnalytics'
import ScrollToTopButton from '@/components/tv/components/ScrollToTopButton'
import { useHomeContent } from '@/pages/home/hooks/useHomeContent'
import styled from 'styled-components'
import { useVODStore } from '@/shared/store'
import { pageType } from '@/shared/store/types'
import { useAtmospheric } from '@/features/VOD/hooks/useAtmospheric'
import { VodRowViewProps } from '@/types/vod/vodRowType'
import {
    vodItemImageHeight,
    vodZeroRowItemImageHeight,
    ZERO_ROW,
} from '@/shared/constants'
import { VodItemsProvider } from '@/features/VOD/context/UseGetVodItemContext/VodItemsContext'
import { getGetZeroRowViewModule } from '@/shared/getContainer/getGetZeroRowViewModule'
import {
    FocusContext,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import VodRowTV from './VOD/components/VodRowTV'
import { VOD_ROW_CONTAINER_FOCUS_KEY } from '@/shared/constants/focusLibraryConstants'

const VodGridContentWrapper = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`
const VodGridScrollingRows = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    flex-shrink: 1;
    flex-grow: 1;
`

const ZeroRowView = await getGetZeroRowViewModule()

export const HomeContentTV = () => {
    const homeContainerRef = useRef<HTMLDivElement | null>(null)
    useEnterPageLogAnalytics('HOME')

    const containerStyle = {
        height: '90vh',
        overflowY: 'auto',
        display: 'flex',
        gap: '50px',
        flexDirection: 'column',
        // padding: '30px',
        // '& > :first-of-type': {
        //     marginBottom: '-50px', // hack to reduce flex gap on first element
        // },
    }

    const scrollToTop = () => {
        const container = homeContainerRef.current
        if (container)
            container?.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
    }
    const vodRowsRef = useRef<HTMLDivElement[]>([])
    const { homeRows } = useHomeContent()
    const { setVodPages } = useVODStore()

    const handleGoToCategoryButtonClick = (
        itemId: string,
        title: string,
        type: pageType
    ): void => {
        setVodPages(itemId, title, type)
        homeContainerRef?.current?.scrollTo(0, 0)
    }

    const { rowElements, handleScroll, handleRefsUpdate } = useAtmospheric(
        homeContainerRef,
        homeRows || []
    )
    useEffect(() => {
        handleRefsUpdate(vodRowsRef.current)
    }, [homeRows?.length, handleRefsUpdate, vodRowsRef.current])

    useEffect(() => {
        const container = homeContainerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [rowElements, homeContainerRef.current])

    // TVAPP code start here

    const {
        ref,
        focusSelf,
        focusKey: HOME_PAGE_FOCUS_KEY,
    } = useFocusable({
        focusKey: VOD_ROW_CONTAINER_FOCUS_KEY,
        focusable: true,
        forceFocus: true,
    })

    const onRowFocus = useCallback(
        ({ top, height }: { top: number; height: number }) => {
            homeContainerRef?.current?.scrollTo({
                top: top - height + 200,
                behavior: 'smooth',
            })
        },
        [ref]
    )

    const childReady = useCallback(() => {
        focusSelf()
    }, [])

    const ZeroRow = useMemo(() => {
        const zeroRow = homeRows.find(
            (row: VodRowViewProps) => row.title.toLowerCase() === ZERO_ROW
        )
        if (!zeroRow) return <></>

        const {
            productId,
            query,
            serviceName,
            hideCaptions,
            hideProgress,
            rowHeightPercentage,
        } = zeroRow

        return (
            <Box
                key={productId}
                data-index={productId}
                ref={(listElement: HTMLDivElement) => {
                    vodRowsRef.current[1] = listElement
                }}>
                <VodItemsProvider
                    productId={String(productId)}
                    type={query || ''}
                    serviceName={serviceName || ''}>
                    <ZeroRowView
                        productId={productId}
                        hideCaptions={hideCaptions}
                        hideProgress={hideProgress}
                        rowHeightPercentage={rowHeightPercentage}
                        maxHeight={vodZeroRowItemImageHeight}
                        handleGoToCategoryButtonClick={
                            handleGoToCategoryButtonClick
                        }
                        isHomeContentRow={true}
                    />
                </VodItemsProvider>
            </Box>
        )
    }, [homeRows, handleGoToCategoryButtonClick])

    const contentToDisplay = useMemo(() => {
        if (!homeRows) return null

        return homeRows.map((row: VodRowViewProps, index: number) => {
            const {
                productId,
                title,
                hideCaptions,
                hideProgress,
                rowHeightPercentage,
                hideTitle,
            } = row

            if (title.toLowerCase() !== ZERO_ROW)
                return (
                    <Box
                        key={productId}
                        data-index={row.productId}
                        ref={(listElement: HTMLDivElement) => {
                            vodRowsRef.current[index] = listElement
                        }}>
                        <VodItemsProvider
                            productId={String(row.productId)}
                            type={row.query || ''}
                            serviceName={row.serviceName || ''}>
                            <VodRowTV
                                productId={productId}
                                title={title}
                                hideTitle={hideTitle}
                                hideCaptions={hideCaptions}
                                hideProgress={hideProgress}
                                rowHeightPercentage={rowHeightPercentage}
                                maxHeight={vodItemImageHeight}
                                handleGoToCategoryButtonClick={
                                    handleGoToCategoryButtonClick
                                }
                                isHomeContentRow={true}
                                onFocusProp={onRowFocus}
                                onReady={childReady}
                            />
                        </VodItemsProvider>
                    </Box>
                )
        })
    }, [homeRows, handleGoToCategoryButtonClick])

    return (
        <FocusContext.Provider value={HOME_PAGE_FOCUS_KEY}>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
                <VodGridContentWrapper>
                    <VodGridScrollingRows ref={ref} id="here">
                        <Box sx={containerStyle} ref={homeContainerRef}>
                            {ZeroRow}

                            {/*<Container maxWidth="xl" disableGutters>*/}
                            <Box sx={{ padding: '0 30px 0 30px' }}>
                                {contentToDisplay}
                            </Box>
                            {/*</Container>*/}

                            <ScrollToTopButton
                                handleOnClick={scrollToTop}
                                container={homeContainerRef.current}
                            />
                        </Box>
                    </VodGridScrollingRows>
                </VodGridContentWrapper>
            </ErrorBoundary>
        </FocusContext.Provider>
    )
}
